import React from 'react';
import { StateCreator } from 'zustand';
import { toast, Slide } from 'react-toastify';
import { NOTIFICATION_CONFIG, NotificationType } from '@/config/notification.config';

interface NotificationIcon {
    className: string;
    color: string;
}

const NOTIFICATION_ICONS = {
    success: { className: 'bi bi-check-circle', color: '#8AC926' },
    error: { className: 'bi bi-exclamation-triangle', color: 'var(--color-error)' },
    warning: { className: 'bi bi-exclamation-triangle', color: '#F49739' },
    info: { className: 'bi bi-info-circle', color: '#18526C' }
} satisfies Partial<Record<NotificationType, NotificationIcon>>;

function createIcon(config: NotificationIcon): React.ReactElement {
    return React.createElement('i', {
        className: config.className,
        style: {
            fontSize: '24px',
            flexShrink: 0,
            color: config.color
        } as React.CSSProperties
    });
}

// ---------------------------------------------------------------------------

export interface NotificationSlice {
    addNotification: (notification: {
        type: NotificationType;
        title: string;
        message?: string;
        duration?: number | false;
    }) => void;
    removeNotification: (id: string | number) => void;
    clearNotifications: () => void;
}

export const createNotificationSlice: StateCreator<NotificationSlice> = () => ({
    addNotification: (notification) => {
        const { type, title, message, duration } = notification;
        const position = NOTIFICATION_CONFIG.notification.position[type];
        const autoClose = duration ?? NOTIFICATION_CONFIG.notification.duration[type];

        const iconConfig = NOTIFICATION_ICONS[type as keyof typeof NOTIFICATION_ICONS];
        const IconComponent = iconConfig ? createIcon(iconConfig) : null;

        const textBlock = React.createElement(
            'div',
            { style: { display: 'flex', flexDirection: 'column' }, className: 'toast-message' },
            React.createElement(
                'div',
                {
                    style: {
                        fontWeight: '400',
                        fontSize: '16px',
                        marginBottom: message ? '4px' : 0,
                        fontFamily: 'var(--font-family-medium)',
                        textTransform: 'uppercase'
                    }
                },
                title
            ),
            message
                ? React.createElement('div', {
                      style: { fontSize: '14px' },
                      dangerouslySetInnerHTML: { __html: message.replace(/\r\n|\n/g, '<br>') }
                  })
                : null
        );

        const content = React.createElement(
            'div',
            {
                style: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '30px'
                }
            },
            IconComponent,
            textBlock
        );

        // Show toast
        toast(content, {
            type,
            position,
            autoClose,
            closeButton: NOTIFICATION_CONFIG.notification.closeButton,
            hideProgressBar: NOTIFICATION_CONFIG.notification.hideProgressBar,
            closeOnClick: NOTIFICATION_CONFIG.notification.closeOnClick,
            pauseOnHover: NOTIFICATION_CONFIG.notification.pauseOnHover,
            pauseOnFocusLoss: NOTIFICATION_CONFIG.notification.pauseOnFocusLoss,
            draggable: NOTIFICATION_CONFIG.notification.draggable,
            icon: false,
            transition: Slide
        });
    },

    removeNotification: (id) => {
        toast.dismiss(id as any);
    },

    clearNotifications: () => {
        toast.dismiss();
    }
});

declare global {
    interface Window {
        appInsights?: {
            trackException: (data: { exception: unknown }) => void;
        };
    }
}
